import type { Theme } from '@mui/material/styles';
import type { CSSObject } from 'styled-components';

import type { TextFieldProps } from './TextField.model';

/**
 * The field's box in px. The label sits outside the input, which is drawn at `mobileScale`
 * below `md`, so a custom start adornment needs these to line its label up with the value.
 * `startAdornmentInset` is MUI's filled-input padding beside a start adornment; the input
 * itself is padded by `theme.new.spacing.medium` with or without one.
 */
export const TEXT_FIELD_METRICS = {
  borderWidth: 1,
  startAdornmentInset: 12,
  labelTranslateX: 12,
  labelShrinkScale: 0.75,
  mobileScale: 0.875,
} as const;

const { borderWidth, labelTranslateX, labelShrinkScale, mobileScale } =
  TEXT_FIELD_METRICS;

export const generateTextFieldCss = (
  theme: Theme,
  props: TextFieldProps
): CSSObject => {
  return {
    '.MuiFormHelperText-root ': {
      margin: '4px 0 0',
    },

    '&.MuiTextField-root': {
      height: 'fit-content',

      '&:focus-within': {
        '&:not(.noFocus)': {
          '& > .MuiInputBase-root': {
            boxShadow: `0px 0px 0px 2px ${theme.new.color.common.white}, 0px 0px 0px 4px ${theme.new.color.brand[500]}`,
          },
        },
      },

      '&.disabled': {
        opacity: '0.5',
      },

      '&.hasIcon': {
        label: {
          left: '30px',
        },
      },

      '&.sensitive': {
        '& > .MuiInputBase-root': {
          paddingRight: '4px',
        },
      },

      label: {
        fontFamily: theme.typography.small_body_regular.fontFamily,
        fontSize: theme.typography.small_body_regular.fontSize,
        fontweight: theme.typography.small_body_regular.fontWeight,
        lineHeight: theme.typography.small_body_regular.lineHeight,

        transform: `translate(${labelTranslateX}px, 13px) scale(1)`,
        '&.MuiInputLabel-shrink': {
          transform: `translate(${labelTranslateX}px, 4px) scale(${labelShrinkScale})`,
        },
      },

      '.MuiInputLabel-root': {
        left: '4px',
      },

      '.MuiInputLabel-filled': {
        '.MuiTypography-root': {
          color: theme.new.color.textSecondary,
        },
      },

      '& > .MuiInputBase-root': {
        backgroundColor: theme.new.color.inputBackground,
        border: `${borderWidth}px solid ${theme.new.color.line.border}`,
        borderRadius: '8px',
        height: '48px',
        lineHeight: theme.typography.small_body_medium.lineHeight,
        width: `calc(100% / ${mobileScale})`,
        transform: `scale(${mobileScale})` /* simulate 14px */,
        transformOrigin: 'left center',
        // Desktop: 14px font size
        [theme.breakpoints.up('md')]: {
          transform: 'none',
          width: '100%',
        },

        '&.MuiInputBase-multiline': {
          height: `${props.multilineHeight}`,

          textarea: {
            paddingLeft: '4px',

            //Important is necessary because MUI forces a runtime-calculated height of 120px.
            height: `calc(${props.multilineHeight} - 32px) !important`,
          },
        },

        '& input': {
          color: theme.new.color.textPrimary,
          fontFamily: theme.typography.small_body_medium.fontFamily,
          fontSize: '1rem', // 16px on mobile to prevent zoom
          [theme.breakpoints.up('md')]: {
            fontSize: '0.875rem', // 14px on desktop
          },
          fontWeight: theme.typography.small_body_medium.fontWeight,
        },

        /* active styles are handled here rather than &:active */
        '&.Mui-focused': {
          borderRadius: '8px',
          borderColor: theme.new.color.actionActive,
        },

        '&:after': {
          content: 'none',
        },

        '&:before': {
          content: 'none',
        },

        '.MuiFilledInput-input': {
          paddingLeft: theme.new.spacing.medium,
        },
      },

      '&.error': {
        '& > .MuiInputBase-root': {
          borderColor: theme.new.color.errorPrimary,
        },
      },
    },
  };
};
